import { useEffect, useCallback, useMemo } from 'react'
import { isArray, mergeWith } from 'lodash'
import { useNavigate } from 'react-router-dom'
import { useReactiveVar } from '@apollo/client'

import { DEFAULT_ENTITIES_LIMIT } from 'shared/consts'
import { useListUsersLazyQuery } from '../graphql/queries'
import { useFormattedZoneList, useLoading, usePrevious, usePreviousNonNullish, useServerError } from 'shared/hooks'
import { usersFiltersVar } from '../store'
import { userSearchNameVar } from 'modulesCommon/store'
import { useZonesList } from 'modulesCommon/serviceHooks'
import { useRoles } from './useRoles'
import { useSpaces } from './useSpaces'

import { IUsersTableData } from '../types'

export const useListUser = () => {
  const navigate = useNavigate()
  const filters = useReactiveVar(usersFiltersVar)
  const searchName = useReactiveVar(userSearchNameVar)
  const prevSearchName = usePrevious(searchName)

  const { zoneList, zoneError, zoneLoading } = useZonesList()
  const { zoneById }= useFormattedZoneList(zoneList)
  const { rolesById, loading: isRolesLoading, error: rolesError } = useRoles()
  const { byId: spacesById } = useSpaces()

  const [getUserList, {
    data: listUserResponse,
    error: listUserError,
    loading: listUserLoading,
    fetchMore: fetchMoreUsers,
    refetch
  }] = useListUsersLazyQuery({ notifyOnNetworkStatusChange: true })

  const createInputFilter = useCallback(
    (offset: number) => ({
      variables: {
        offset,
        limit: DEFAULT_ENTITIES_LIMIT,
        name: searchName,
        filters
      }
    }),
    [searchName, filters]
  )

  const usersList = listUserResponse?.listUsers?.users ?? []
  const total = parseInt(listUserResponse?.listUsers?.total ?? '0')
  const prevUserList = usePreviousNonNullish(listUserResponse?.listUsers?.users)

  const increaseUsersOffset = () => {
    if (!listUserLoading) {
      fetchMoreUsers({
        variables: {
          offset: usersList.length
        },
        updateQuery: (prev, { fetchMoreResult, variables: { offset } }) => {
          if (!fetchMoreResult) return prev
          return mergeWith(fetchMoreResult, prev,(objValue, srcValue) => {
            if (isArray(objValue)) {
              return [ ...srcValue, ...objValue]
            }
          })
        }
      })
    }
  }

  const tableUserList: IUsersTableData[] = useMemo(
    () =>
      (listUserResponse?.listUsers?.users ?? prevUserList ?? []).map((user) => ({
        id: user.id ?? '',
        email: user.email ?? '',
        name: `${user.first_name} ${user.last_name}`,
        label: (spacesById[user.space_id ?? ''] ?? zoneById[user.label_id ?? ''])?.name ?? '',
        role: rolesById[user.role?.role_id ?? '']?.name || '',
        editLink: `/admin/users/${user.id}`
      })),
    [rolesById, spacesById, zoneById, listUserResponse?.listUsers, prevUserList]
  )

  const onNewUser = () => navigate('/admin/users/new')
  const onUpdateUser = useCallback(
    (url) => () => {
      navigate(url)
    },
    [navigate]
  )

  useEffect(() => {
    getUserList(createInputFilter(0))
  }, [createInputFilter, getUserList])

  useEffect(() => {
    const isSearchNameChanged = searchName.length >= 3 || (prevSearchName && prevSearchName.length > searchName.length)
    if (isSearchNameChanged) {
      refetch()
    }
  }, [searchName, prevSearchName, createInputFilter, getUserList, refetch])

  useEffect(() => {
    if (filters) {
      refetch()
    }
  }, [filters, refetch])

  useLoading({ isLoading: listUserLoading || zoneLoading || isRolesLoading })
  useServerError([listUserError, zoneError, rolesError])

  return {
    searchName,
    userSearchNameVar,
    onNewUser,
    onUpdateUser,
    tableUserList,
    total,
    listUserError,
    listUserLoading,
    increaseUsersOffset
  }
}
